import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * The colours of the outcome groups and the call types (ADR-160, ACL-508), kept here because
 * nothing outside the design system picks a colour. The tokens are `--color-outcome-<group>` and
 * `--color-call-<type>` in `index.css`; a colour always goes with words, never alone.
 */

/** A group's colour as a CSS value, for a stripe or a dot. */
export function outcomeColor(group: string): string {
  return `var(--color-outcome-${group})`;
}

/** The background and border of a tile in a group's colour: a tint over the surface. */
export function outcomeTint(group: string, strength = 9): React.CSSProperties {
  const color = outcomeColor(group);
  return {
    background: `color-mix(in srgb, ${color} ${strength}%, var(--color-surface))`,
    borderColor: `color-mix(in srgb, ${color} 45%, transparent)`,
  };
}

/** The group's words in its colour, dimmed toward the text so they stay readable. */
export function outcomeInk(group: string): React.CSSProperties {
  return { color: `color-mix(in srgb, ${outcomeColor(group)} 75%, var(--color-text))` };
}

/** A dot in a group's colour, beside its name. */
export function OutcomeDot({ group, className }: { group: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn("inline-block size-2 shrink-0 rounded-full", className)}
      style={{ background: outcomeColor(group) }}
    />
  );
}

/** The thin bar at a row's left edge, in a group's colour. */
export function OutcomeStripe({ group, className }: { group: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn("absolute top-1 bottom-1 left-0 w-[3px] rounded-full", className)}
      style={{ background: outcomeColor(group) }}
    />
  );
}

/** A call type's chip colours: `inbound` or `dialer`. */
export function callTypeStyle(type: "inbound" | "dialer"): React.CSSProperties {
  const color = `var(--color-call-${type})`;
  return {
    color: `color-mix(in srgb, ${color} 85%, var(--color-text))`,
    borderColor: `color-mix(in srgb, ${color} 40%, transparent)`,
    background: `color-mix(in srgb, ${color} 10%, var(--color-surface))`,
  };
}

/**
 * An outcome's name as a small tag in its group's colour, with the group's dot: how an outcome
 * reads on every screen outside the window after a call (the lead card, the timeline, the contact's
 * record, the lists). `group` null, for a slug the screen's catalog does not hold, is the plain
 * tag: no colour is guessed.
 */
export function OutcomeTag({
  group, size = "sm", className, children, ...props
}: React.HTMLAttributes<HTMLSpanElement> & { group: string | null; size?: "sm" | "md" }) {
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1 rounded border font-medium whitespace-nowrap",
        size === "sm" ? "h-4 px-1.5 text-2xs" : "h-5 px-2 text-xs",
        group === null && "border-border text-text-muted",
        className,
      )}
      style={group === null ? undefined : { ...outcomeTint(group, 8), ...outcomeInk(group) }}
      {...props}
    >
      {group !== null && <OutcomeDot group={group} className={size === "sm" ? "size-1.5" : undefined} />}
      <span className="truncate">{children}</span>
    </span>
  );
}
